import React from "react";
import { useAppDispatch, useAppSelector } from "store";
import _ from "lodash";

import { Box } from "@material-ui/core";
import { MapRef, ViewportProps } from "react-map-gl";

import { selectPinnedLocation } from "modules/customer/tools/location/locationSlice";

import {
    selectIsLoading,
    selectHasErrors,
    selectPinnedLocationNearbyStores,
    selectNearbyStoresDownloadData,
    selectViewport,
    setViewport
} from "modules/customer/tools/location/retailers/retailersSlice";
import { NearbyStore } from "modules/customer/tools/location/retailers/nearbyStore";
import { selectMapboxConfiguration } from "modules/siteConfiguration/siteConfigurationSlice";

import MapBase from "components/visuals/maps/MapBase";
import Explore from "pages/customer/tools/location/map/Explore";
import PinnedLocation from "pages/customer/tools/location/map/PinnedLocation";

import Legend from "./Legend";
import Marker from "./Marker";
import mathUtils from "utils/mathUtils";

const Map: React.FC = () => {
    const dispatch = useAppDispatch();
    const mapRef = React.useRef<MapRef>(null);
    const mapboxConfiguration = useAppSelector(selectMapboxConfiguration);
    const isLoading = useAppSelector(selectIsLoading);
    const hasErrors = useAppSelector(selectHasErrors);
    const nearbyStores = useAppSelector(selectPinnedLocationNearbyStores);
    const nearbyStoresDownloadData = useAppSelector(selectNearbyStoresDownloadData);
    const pinnedLocation = useAppSelector(selectPinnedLocation);
    const viewport = useAppSelector(selectViewport);

    const [hoveredNearbyStore, setHoveredNearbyStore] = React.useState<NearbyStore | undefined>(undefined);
    const [initialViewport, setInitialViewport] = React.useState({
        latitude: pinnedLocation?.latitude ?? 0,
        longitude: pinnedLocation?.longitude ?? 0,
        zoom: 15.5,
        minZoom: 4
    });

    React.useEffect(() => {
        setInitialViewport(current => {
            return {
                ...current,
                latitude: pinnedLocation?.latitude ?? 0,
                longitude: pinnedLocation?.longitude ?? 0
            };
        });
    }, [pinnedLocation]);

    const downloadData = React.useMemo(() => {
        return nearbyStoresDownloadData.map(nearbystore => {
            return {
                "Store name": nearbystore.storeName,
                "Brand name": nearbystore.brandName,
                "Distance from selected location (km)": mathUtils.round(nearbystore.distanceToSelectedLocation, 4),
                "Store category": nearbystore.placeCategoryId,
                "Store super category": nearbystore.placeSuperCategory,
                "Store size": nearbystore.sizeBucket?.label ?? "Unknown",
                "Size range (square feet)": nearbystore.sizeBucket ? `${nearbystore.sizeBucket.lowerBound} - ${nearbystore.sizeBucket.upperBound}` : "",
                "Brand classification": nearbystore.brandClassification ?? "Unclassified"
            };
        });
    }, [nearbyStoresDownloadData]);

    const handleViewportChange = React.useCallback((newViewport: ViewportProps) => {
        dispatch(setViewport(newViewport));
        const map = mapRef?.current?.getMap();
        if (!map) {
            return;
        }
    }, [dispatch, mapRef]);

    const handleViewportChangeDelayed = React.useMemo(() => {
        return _.debounce((newViewport: ViewportProps) => handleViewportChange(newViewport), 500);
    }, [handleViewportChange]);

    return (
        <Box data-cy="competition-map">
            <MapBase
                loading={isLoading}
                error={hasErrors}
                mapboxAccessToken={mapboxConfiguration.accessToken}
                mapboxBaseMapStyle={mapboxConfiguration.baseMapStyle}
                height="100vh"
                initialViewport={initialViewport}
                addGeocoder={false}
                addNavigationControl={true}
                addRecenterButton={true}
                dataCy="nearbystores-map"
                mapRef={mapRef}
                downloadData={downloadData}
                addFullscreenButton={true}
                onViewportChange={handleViewportChangeDelayed}
            >
                <Explore />
                {nearbyStores.map((nearbystore, index) =>
                    <Marker
                        key={index}
                        nearbyStore={nearbystore}
                        zoomLevel={viewport?.zoom ?? initialViewport.zoom}
                        onTooltipOpen={() => setHoveredNearbyStore(nearbystore)}
                        onTooltipClose={() => setHoveredNearbyStore(undefined)}
                    />
                )}
                <PinnedLocation />
                <Legend
                    nearbyStore={hoveredNearbyStore}
                    pinnedLocation={pinnedLocation}
                />
            </MapBase>
        </Box>
    );
};

export default Map;
